import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { getSubjectiveExam, getSubmissions, evaluateSubmission, publishResults, evaluateWithAI } from "../services/subjective_exams";
import { Loader2, CheckCircle, FileText, ChevronLeft, Save, Sparkles } from "lucide-react";
import toast, { Toaster } from 'react-hot-toast';
import api from "../services/api";

export default function TeacherSubjectiveEvaluation() {
  const { examId } = useParams();
  const navigate = useNavigate();

  const [exam, setExam] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [activeSubmission, setActiveSubmission] = useState(null);
  const [evaluations, setEvaluations] = useState({});
  const [aiLoading, setAiLoading] = useState({});

  useEffect(() => {
    loadData();
  }, [examId]);

  const loadData = async () => {
    try {
      const [examData, subsData] = await Promise.all([
        getSubjectiveExam(examId),
        getSubmissions(examId)
      ]);
      setExam(examData);
      setSubmissions(subsData);
    } catch (err) {
      toast.error("Failed to load evaluation data");
    } finally {
      setLoading(false);
    }
  };

  const openSubmission = (sub) => {
    setActiveSubmission(sub);
    // Initialize evaluations state if they already have them
    const evals = {};
    exam.questions.forEach(q => {
      evals[q.id] = { marks_awarded: 0, comments: "" }; // default
    });
    setEvaluations(evals);
  };

  const handleEvalChange = (qId, field, value) => {
    setEvaluations(prev => ({
      ...prev,
      [qId]: {
        ...prev[qId],
        [field]: value
      }
    }));
  };

  const saveEvaluation = async (qId) => {
    try {
      await evaluateSubmission(activeSubmission.id, qId, evaluations[qId]);
      toast.success("Marks saved for question");
    } catch (err) {
      toast.error("Failed to save marks");
    }
  };

  const handleEvaluateAI = async (qId) => {
    setAiLoading(prev => ({ ...prev, [qId]: true }));
    const loadingToast = toast.loading("AI is grading this question...");
    try {
      const result = await evaluateWithAI(activeSubmission.id, qId);
      setEvaluations(prev => ({
        ...prev,
        [qId]: {
          marks_awarded: result.marks_awarded,
          comments: result.comments
        }
      }));
      toast.success("AI generated a grading suggestion!", { id: loadingToast });
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI Evaluation failed.", { id: loadingToast });
    } finally {
      setAiLoading(prev => ({ ...prev, [qId]: false }));
    }
  };

  const handlePublish = async () => {
    try {
      await publishResults(activeSubmission.id);
      toast.success("Results published!");
      setActiveSubmission(null);
      loadData();
    } catch (err) {
      toast.error("Failed to publish results");
    }
  };

  if (loading) return <div className="flex h-screen items-center justify-center"><Loader2 className="animate-spin text-indigo-600" size={48} /></div>;

  return (
    <div className="flex h-screen bg-gray-50">
      <Toaster position="top-right" />
      <Sidebar role="teacher" />
      <div className="flex-1 overflow-hidden flex flex-col">
        {!activeSubmission ? (
          <div className="p-8 overflow-y-auto">
            <div className="max-w-6xl mx-auto">
              <button onClick={() => navigate('/teacher-subjective-exams')} className="text-gray-500 hover:text-gray-800 flex items-center gap-1 mb-4">
                <ChevronLeft size={20} /> Back to Exams
              </button>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">Submissions: {exam.title}</h1>
              <p className="text-gray-600 mb-8">Evaluate student answer sheets below.</p>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 font-medium">
                      <th className="p-4">Student ID</th>
                      <th className="p-4">Status</th>
                      <th className="p-4">Submitted At</th>
                      <th className="p-4">Total Score</th>
                      <th className="p-4">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {submissions.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="p-8 text-center text-gray-500">No submissions yet.</td>
                      </tr>
                    ) : (
                      submissions.map(sub => (
                        <tr key={sub.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="p-4 font-medium text-gray-800">#{sub.student_id}</td>
                          <td className="p-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${sub.status === 'evaluated' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                              {sub.status.toUpperCase()}
                            </span>
                          </td>
                          <td className="p-4 text-gray-600">{new Date(sub.submitted_at).toLocaleString()}</td>
                          <td className="p-4 font-bold text-gray-900">{sub.status === 'evaluated' ? sub.total_score : '--'} / {exam.total_marks}</td>
                          <td className="p-4">
                            <button
                              onClick={() => openSubmission(sub)}
                              className="text-indigo-600 font-medium hover:text-indigo-800"
                            >
                              Evaluate
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex h-full">
            {/* Answer Sheet Viewer (Left) */}
            <div className="flex-1 border-r bg-gray-900 p-4 flex flex-col">
              <div className="flex justify-between items-center mb-4 text-white">
                <h3 className="font-semibold">Student #{activeSubmission.student_id} Answer Sheet</h3>
                <a
                  href={activeSubmission.pdf_url?.startsWith('http') ? activeSubmission.pdf_url : api.defaults.baseURL.replace('/api', '') + activeSubmission.pdf_url}
                  target="_blank"
                  rel="noreferrer"
                  className="bg-gray-800 hover:bg-gray-700 px-3 py-1 rounded text-sm flex items-center gap-2"
                >
                  <FileText size={16} /> Open Full PDF
                </a>
              </div>
              <div className="flex-1 bg-gray-800 rounded-lg overflow-hidden flex items-center justify-center">
                {activeSubmission.pdf_url ? (
                  <iframe
                    src={activeSubmission.pdf_url.startsWith('http') ? activeSubmission.pdf_url : api.defaults.baseURL.replace('/api', '') + activeSubmission.pdf_url}
                    className="w-full h-full border-0"
                    title="Answer Sheet"
                  />
                ) : (
                  <p className="text-gray-400">PDF not generated properly.</p>
                )}
              </div>
            </div>

            {/* Grading Panel (Right) */}
            <div className="w-[450px] bg-white flex flex-col overflow-y-auto">
              <div className="p-4 border-b bg-gray-50 flex items-center justify-between sticky top-0 z-10">
                <h2 className="font-bold text-lg text-gray-800">Grading Panel</h2>
                <button onClick={() => setActiveSubmission(null)} className="text-gray-500 hover:text-gray-800 text-sm font-medium">Close</button>
              </div>

              <div className="p-4 space-y-6">
                {exam.questions.map((q, i) => (
                  <div key={q.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50 relative">
                    <div className="font-medium text-gray-700 mb-2 pr-28">Q{i + 1}: {q.prompt}</div>
                    <div className="text-sm text-gray-500 mb-4">Max Marks: {q.marks}</div>
                    
                    <button 
                      onClick={() => handleEvaluateAI(q.id)}
                      disabled={aiLoading[q.id]}
                      className="absolute top-4 right-4 bg-indigo-100 text-indigo-700 hover:bg-indigo-200 px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 transition disabled:opacity-50"
                    >
                      {aiLoading[q.id] ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                      AI Grade
                    </button>

                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Marks Awarded</label>
                        <input
                          type="number" min="0" max={q.marks} step="0.5"
                          value={evaluations[q.id]?.marks_awarded || 0}
                          onChange={(e) => handleEvalChange(q.id, 'marks_awarded', parseFloat(e.target.value))}
                          className="w-full border p-2 rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Teacher Comments</label>
                        <textarea
                          rows="2"
                          value={evaluations[q.id]?.comments || ""}
                          onChange={(e) => handleEvalChange(q.id, 'comments', e.target.value)}
                          className="w-full border p-2 rounded-lg"
                          placeholder="Feedback..."
                        />
                      </div>
                      <button
                        onClick={() => saveEvaluation(q.id)}
                        className="w-full bg-indigo-50 text-indigo-700 py-2 rounded-lg font-medium hover:bg-indigo-100 flex justify-center items-center gap-2"
                      >
                        <Save size={16} /> Save Q{i + 1} Marks
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-4 border-t mt-auto sticky bottom-0 bg-white shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
                <div className="flex justify-between items-center mb-4">
                  <span className="font-medium text-gray-700">Total Marks</span>
                  <span className="font-bold text-xl text-indigo-600">
                    {Object.values(evaluations).reduce((sum, e) => sum + (e.marks_awarded || 0), 0)} / {exam.total_marks}
                  </span>
                </div>
                <button
                  onClick={handlePublish}
                  className="w-full bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 flex justify-center items-center gap-2"
                >
                  <CheckCircle size={20} /> Publish Result
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
